import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ApplicationForm from "../components/Public/ApplicationForm";
import Header from "../components/Public/ui/Header";

const initialState = {
  serviceType: "Normal",
  documentType: "All Countries",
  sludiNumber: "",
  birthDate: { day: "", month: "", year: "" },
  gender: "",
  surname: "",
  otherNames: "",
  permanentAddress: { line1: "", line2: "", city: "", district: "" },
  birthCertificateNo: "",
  birthDistrict: "",
  placeOfBirth: "",
  occupation: "",
  hasDualCitizenship: false,
  dualCitizenshipNo: "",
  foreignNationality: "",
  foreignPassportNo: "",
  mobileNumber: "",
  emailAddress: "",
  needPhotoAtLocation: true,
  photoAcknowledgmentNo: "",
  presentTravelDocNo: "",
  nmrpNo: "",
  profilePicture: null,
};

export default function CitizenPassportForm() {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [applicationData, setApplicationData] = useState(initialState);
  const [lockedFields, setLockedFields] = useState([]);
  const [isRenewal, setIsRenewal] = useState(false);

  useEffect(() => {
    // Load Barlow font
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const renewalDataState = location.state?.renewalData;
    const renewalFlag = location.state?.isRenewal;
    const renewalData = renewalDataState?.applicationData || renewalDataState;

    if (renewalFlag && renewalData) {
      console.log("🔄 Renewal flow detected. Pre-filling form with renewal data:", renewalData);

      setApplicationData((prev) => ({
        ...prev,
        profilePicture: renewalData.profile_picture,
        sludiNumber: renewalData.sludi_number || "",
        birthDate: renewalData.birth_date
          ? (() => {
            const isoDate = new Date(renewalData.birth_date).toISOString().split("T")[0];
            const [year, month, day] = isoDate.split("-");
            return { year, month, day };
          })()
          : { day: "", month: "", year: "" },
        birthCertFront: renewalData.birth_cert_front || "",
        gender: renewalData.gender || "",
        surname: renewalData.surname || "",
        otherNames: renewalData.other_names || "",
        permanentAddress: {
          line1: renewalData.address_line1 || "",
          line2: renewalData.address_line2 || "",
          city: renewalData.city || "",
          district: renewalData.district || "",
        },
        birthCertificateNo: renewalData.birth_certificate_no || "",
        birthDistrict: renewalData.birth_district || "",
        placeOfBirth: renewalData.place_of_birth || "",
        occupation: renewalData.profession || "",
        hasDualCitizenship: renewalData.dual_citizenship || false,
        dualCitizenshipNo: renewalData.dual_citizenship_no || "",
        foreignNationality: renewalData.foreign_nationality || "",
        foreignPassportNo: renewalData.foreign_passport_no || "",
        mobileNumber: renewalData.mobile_number || "",
        emailAddress: renewalData.email_address || "",
        presentTravelDocNo: renewalData.present_travel_document_no || "",
        nmrpNo: renewalData.nmrpNo || "",
        previousApplicationNumber: renewalData.application_number,
        previousApplicationId: renewalData.id,
        previousBirthCertificateFront: renewalData.birth_cert_front,
      }));

      setLockedFields([
        "sludiNumber", "birthDate", "gender", "surname", "otherNames",
        "birthCertificateNo", "birthDistrict", "placeOfBirth", "emailAddress",
        "permanentAddress.line1", "permanentAddress.line2",
        "permanentAddress.city", "birthCertFront"
      ]);

      setIsRenewal(true);
      console.log("✅ Form pre-filled with renewal data");
    } else {
      console.log("➡️ Normal application flow. Fetching user info from backend...");

      fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/user`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.userData) {
            const user = data.userData;
            const addressString = user.address?.street_address?.trim() || "";
            const parts = addressString.split(/\s+/).filter(Boolean);

            let line1 = "", line2 = "", city = "", district = "";
            if (parts.length >= 5) {
              line1 = parts.slice(0, 2).join(" ");
              line2 = parts.slice(2, 4).join(" ");
              city = parts[4];
              district = parts[5] || "";
            } else {
              line1 = addressString;
            }

            setApplicationData((prev) => ({
              ...prev,
              sludiNumber: user.sludiNumber || "",
              emailAddress: user.email || "",
              mobileNumber: user.phone || "",
              surname: user.name?.split(" ").slice(-1).join("") || "",
              otherNames: user.name?.split(" ").slice(0, -1).join(" ") || "",
              gender: user.gender || "",
              birthDate: user.birthdate
                ? {
                  year: user.birthdate.split("/")[0],
                  month: user.birthdate.split("/")[1],
                  day: user.birthdate.split("/")[2],
                }
                : { year: "", month: "", day: "" },
              profilePicture: user.picture,
              permanentAddress: { line1, line2, city, district },
            }));

            setLockedFields([
              "sludiNumber", "surname", "otherNames", "birthDate", "gender", "emailAddress",
              "permanentAddress.line1", "permanentAddress.line2",
              "permanentAddress.city", "permanentAddress.district"
            ]);

            console.log("✅ Form prefilled with user:", user);
          }
        })
        .catch((err) => console.error("🔥 Failed to fetch user:", err));
    }
  }, [location.state]);

  const setField = (name, value) => {
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setApplicationData((p) => ({
        ...p,
        [parent]: { ...p[parent], [child]: value },
      }));
    } else {
      setApplicationData((p) => ({ ...p, [name]: value }));
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: "#f8fafc",
      fontFamily: "'Barlow', sans-serif",
      backgroundImage: "url('/assets/Images/SLUDI-background.png')",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed",
    }}>

      <Header />

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "clamp(1rem, 4vw, 2rem)" }}>
        {/* Renewal Banner */}
        {isRenewal && (
          <div style={{
            backgroundColor: "#eff6ff",
            border: "2px solid #3b82f6",
            borderRadius: "12px",
            padding: "1rem 1.5rem",
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            boxShadow: "0 2px 4px rgba(59, 130, 246, 0.1)",
          }}>
            <span style={{ fontSize: "28px" }}>🔄</span>
            <div>
              <h3 style={{
                margin: 0,
                color: "#1e40af",
                fontSize: "1.1rem",
                fontWeight: "600",
                fontFamily: "'Barlow', sans-serif",
              }}>
                Renewal Application
              </h3>
              <p style={{
                margin: "4px 0 0 0",
                color: "#1e40af",
                fontSize: "0.9rem",
                fontFamily: "'Barlow', sans-serif",
              }}>
                Your previous application details have been pre-filled. Some fields are locked and cannot be changed.
              </p>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div style={{
          backgroundColor: "white",
          borderRadius: "0.75rem",
          padding: "clamp(1rem, 4vw, 2rem)",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}>
          {currentStep === 1 && (
            <ApplicationForm
              data={applicationData}
              setField={setField}
              onNextStep={() => setCurrentStep(2)}
              lockedFields={lockedFields}
              isRenewal={isRenewal}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{
          textAlign: "center",
          padding: "clamp(1rem, 4vw, 2rem)",
          color: "#6b7280",
          fontSize: "0.875rem",
          fontFamily: "'Barlow', sans-serif",
        }}>
          © 2026 Department of Immigration & Emigration. All Rights Reserved
        </div>
      </div>
    </div>
  );
}